//Library Imports
import React, { FC } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";

//Local Imports
import BlankProfilePhoto from "assets/img/blank_profile_photo.png";
import TableSort from "modules/Common/Components/TableSort";
import { getUnsubscribedContactsList } from "./action";
import UnsubContactsLoader from "./Loaders/UnsubContactsLoader";
import { getPagination, RedirectToContactDetails } from "./utils";

//Style Imports
import styled from "styled-components";

interface IUnsubscribedContactsList {
  page?: string;
  tab?: string;
  searchedText: string;
  setSearchedText: (value: string) => void;
}

const UnsubscribedContactsList: FC<IUnsubscribedContactsList> = (props: IUnsubscribedContactsList) => {
  //props constants
  const { searchedText, setSearchedText } = props;

  //Redux States
  const unsubscribed = useSelector((state: RootState) => state.contactAnalytics.unsubscribed);
  const loading = useSelector((state: RootState) => state.contactAnalytics.unsubscribedLoading);
  const total_unsubscribed = useSelector((state: RootState) => state.contactAnalytics.total_unsubscribed);
  const columns = React.useMemo(
    () => [
      {
        id: "main",
        hideHeader: true,
        disableSortBy: true,
        columns: [
          {
            Header: "Contact",
            disableSortBy: true,
            accessor: "contact_name",
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div>
                  <div className="preview">
                    <img
                      style={{
                        boxShadow: item.personality.color ? `0px 0px 0px 2px ${item.personality.color}` : "",
                      }}
                      src={
                        item.contact_image !== null && item.contact_image !== ""
                          ? item.contact_image
                          : BlankProfilePhoto
                      }
                      alt=""
                      onError={(e: any) => {
                        e.target.onerror = null;
                        e.target.src = BlankProfilePhoto;
                      }}
                    />
                  </div>
                  <div className="details">
                    <>
                      {item.contact_name === null ||
                      (item.contact_email !== null && item.contact_name?.trim() === "")
                        ? item.contact_email
                        : item.contact_name}
                      <div className="company">{item.company_name}</div>
                      <div className="designation">{item.job_title}</div>
                    </>
                  </div>
                </div>
              );
            },
          },
          {
            Header: "Email",
            disableSortBy: true,
            accessor: "contact_email",
          },
          {
            Header: "Company",
            disableSortBy: true,
            accessor: "company_name",
            Cell: (props: any) => {
              let item = props.row.original;
              return <div className="company-name">{item.company_name}</div>;
            },
          },
          {
            Header: "Date & Time",
            disableSortBy: true,
            accessor: "timestamp",
          },
        ],
      },
    ],
    []
  );

  const getParams = (params?: any) => {
    return getPagination("unsubscribed", {
      searchWord: searchedText,
      offset: params !== undefined ? 0 : unsubscribed.length > 0 ? unsubscribed.length : 0,
    });
  };

  return (
    <Wrapper>
      {unsubscribed !== undefined ? (
        <>
          <TableSort
            columns={columns}
            data={unsubscribed}
            module={"unsubscribed"}
            _onRowClick={RedirectToContactDetails}
            next={() => getUnsubscribedContactsList(getParams())}
            hasMore={unsubscribed?.length < total_unsubscribed}
            loader={[1, 2, 3].map((item) => (
              <UnsubContactsLoader key={item} loading={loading} />
            ))}
            loading={loading}
            wrapperClassName={unsubscribed.length > 0 ? "unsubscribed-contacts-list" : ""}
          />
          {!loading && unsubscribed?.length === 0 ? <div className="no-data">No Data Found!</div> : null}
        </>
      ) : (
        <div className="no-data">No Data Fouand!</div>
      )}
    </Wrapper>
  );
};

export default UnsubscribedContactsList;

const Wrapper = styled.div`
  background-color: transparent;
  overflow: hidden;
  width: 100%;
  margin-bottom: 10px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    min-height: 83px;
  }
  > div {
    width: 100%;
  }
  svg {
    rect.email {
      x: 25%;
      width: 12%;
      @media (max-width: 1621px) {
        x: 41%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 29%;
        y: 45%;
        width: 15%;
      }
    }

    rect.company {
      x: 44%;
      @media (max-width: 1621px) {
        x: 61%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 53%;
        y: 45%;
      }
    }

    rect.dateTime {
      x: 54%;
      @media (max-width: 1621px) {
        x: 72%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 63%;
        y: 45%;
      }
    }
  }
  .no-data {
    width: 100%;
    height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: "robotomedium";
    color: #67696a;
    font-size: 1rem;
    margin-bottom: 30px;
    /* box-shadow: 6px 30px 73px -10px grey; */
    border-radius: 5px;
  }
  .unsubscribed-contacts-list {
    height: calc(100vh - 275px);
    @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
      height: calc(100vh - 235px);
    }
  }
  table {
    width: 100%;
    margin-top: 32px;
    thead {
      border-bottom: 1px solid #dddddd;
      tr {
        padding: 0 15px;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          padding-right: 0px;
        }
        th {
          color: #5e7079;
          font-weight: 700;
          font-size: 18px;
          font-family: "Roboto", sans-serif;
        }
      }
    }
    tbody {
      width: 100%;
      display: block;
      box-sizing: border-box;
      overflow-y: auto;
      height: calc(100vh - 340px);
      /* padding-bottom: 90px; */
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: calc(100vh - 300px);
      }
      scrollbar-width: thin;
      scrollbar-color: #001760 #efefef;
      &::-webkit-scrollbar {
        width: 5px;
        height: 5px;
        border-radius: 5px;
      }
      /* Track */
      &::-webkit-scrollbar-track {
        background: #fff;
        margin-top: 0;
        margin-bottom: 10px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 0;
        }
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #001760;
      }
      /* Handle on hover */
      /* &::-webkit-scrollbar-thumb:hover {
        background: #efefef;
      } */
      > div {
        overflow: hidden;
      }
      tr {
        background-color: #ffffff;
        box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
        width: 100%;
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        margin: 4px 0;
        padding: 16px 15px 10px 15px;
        cursor: pointer;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          padding-right: 0px;
        }
      }
    }
    thead {
      tr {
        th {
          span {
            width: 15px;
            .sort_asc {
              width: 10px;
              height: 8px;
              margin-top: -15px;
              margin-left: 5px;
              float: left;
              @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                margin-top: -13px;
              }
              img {
                transform: rotate(180deg);
              }
            }
            .sort_desc {
              width: 10px;
              height: 8px;
              float: left;
              margin-left: 5px;
              margin-top: 5px;
              img {
                float: left;
              }
            }
          }
        }
      }
      tr:nth-child(2) {
        th {
          font-size: 16px;
          color: #5e7079;
          font-weight: 500;
          font-family: "Roboto", sans-serif;
          white-space: nowrap;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
          &:nth-child(1) {
            padding-left: 75px;
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              padding-left: 15px;
            }
          }
        }
      }
    }
    thead,
    tbody {
      width: 100%;
      tr {
        width: 100%;
        display: block;
        float: left;
        td {
          min-height: 70px;
          font-family: "Roboto", sans-serif;
          font-weight: 500;
          font-size: 20px;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
        }
        th,
        td {
          display: block;
          float: left;
          &[role="cell"] {
            color: #424b50;
          }
          &:nth-child(1) {
            width: calc(70% - 550px);
            text-align: left;
            @media all and (max-width: 1439px) {
              width: calc(75% - 400px);
            }
            > div {
              display: flex;
              width: 100%;
              align-items: center;
              flex-direction: row;
              min-height: 65px;
              .preview {
                float: left;
              }
              .details {
                float: left;
                font-weight: 400;
                color: #000000;
                font-size: 20px;
                font-family: "Roboto Slab", serif;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
                @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 14px;
                }
                .company {
                  color: #424b50;
                  font-weight: 500;
                  font-size: 14px;
                  font-family: "Roboto", sans-serif;
                  white-space: nowrap;
                  text-overflow: ellipsis;
                  overflow: hidden;
                  display: block;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    font-size: 12px;
                  }
                }
                .designation {
                  color: #27ce0a;
                  font-weight: 500;
                  font-size: 14px;
                  font-family: "Roboto", sans-serif;
                  white-space: nowrap;
                  text-overflow: ellipsis;
                  overflow: hidden;
                  display: block;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    font-size: 12px;
                  }
                }
              }
            }
          }
          &:nth-child(2) {
            width: 30%;
            text-align: left;
            display: inline-flex;
            align-items: center;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            word-break: keep-all;
            &[role="cell"] {
              color: #27ce0a;
              font-size: 16px;
              @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 12px;
              }
            }
            @media all and (max-width: 1439px) {
              width: 25%;
            }
          }
          &:nth-child(3) {
            width: 350px;
            text-align: left;
            display: inline-flex;
            align-items: center;
            @media all and (max-width: 1439px) {
              width: 200px;
            }
            .company-name {
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              text-overflow: ellipsis;
              word-break: break-word;
            }
          }
          &:nth-child(4) {
            width: 200px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
        }
      }
    }
  }
  .engagement {
    border-radius: 12px;
    background-color: #1971eb;
    color: #ffffff;
    font-size: 16px;
    font-family: "robotomedium";
    font-weight: 500;
    padding: 0px 10px;
    display: inline;
    float: right;
  }
  .blog-image {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .blog-image {
    .img1 {
      height: 70px;
      width: 52px;
      border-radius: 6px 0px 0px 6px;
      margin-right: 2px;
      @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
        border-radius: 4px 0px 0px 4px;
        height: 50px;
        width: 36px;
      }
    }
    .img-bg {
      background-position: center center;
      background-size: cover;
      background-repeat: no-repeat;
    }
    .right-images {
      .overlayed-image {
        position: absolute;
        left: 9%;
        top: 22%;
      }
      .img-bg {
        background-position: center center;
        background-size: cover;
        background-repeat: no-repeat;
      }
      display: flex;
      flex-direction: column;
      .img2 {
        height: 34px;
        width: 39px;
        border-radius: 0px 6px 0px 0px;
        margin-bottom: 1px;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          border-radius: 0px 4px 0px 0px;
          height: 25px;
          width: 28px;
        }
      }
      .img3 {
        height: 34px;
        width: 39px;
        border-radius: 0px 0px 6px 0px;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          border-radius: 0px 0px 4px 0px;
          height: 23px;
          width: 28px;
        }
      }
    }
  }
  .disabled {
    pointer-events: none !important;
    background-color: rgba(225, 225, 225, 0.7) !important;
    cursor: no-drop;
  }
  .preview {
    position: relative;
    max-width: 55px;
    -ms-flex-negative: 1 !important;
    flex-shrink: 1 !important;
    img {
      border-radius: 100%;
      max-width: 100%;
      height: 55px;
      width: 55px;
      min-width: 55px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: 40px;
        width: 40px;
        min-width: 40px;
      }
    }
  }
  .details {
    padding-left: 15px;
    .name,
    .company,
    .designation,
    .recipient-count {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 0px;
      -webkit-box-align: start;
      width: 100%;
      word-break: break-word;
    }
    .name {
      color: #000000;
      font-size: 20px;
      font-weight: 400;
      line-height: 22px;
      font-family: "Roboto Slab", serif;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 15px;
        font-size: 13px;
      }
    }
    .list-name {
      color: #000000;
      font-size: 20px;
      font-weight: 400;
      line-height: 22px;
      font-family: "Roboto Slab", serif;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;

      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 14px;
        font-size: 13px;
      }
    }
    .company {
      -webkit-line-clamp: 1;
      font-family: "Roboto", sans-serif;
      font-weight: 500;
      font-size: 14px;
      line-height: 16px;
      color: #424b50;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
    }
    .designation {
      -webkit-line-clamp: 1;
      font-family: "Roboto", sans-serif;
      font-weight: 500;
      line-height: 16px;
      font-size: 14px;
      color: #27ce0a;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
    }
    .recipient-count {
      -webkit-line-clamp: 1;
      font-family: "Roboto", sans-serif;
      font-weight: 500;
      line-height: 16px;
      font-size: 14px;
      color: #27ce0a;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
    }
  }
  .actions {
    -ms-flex-negative: 1;
    flex-shrink: 1;
    align-self: center;
    margin-left: auto;
    min-width: 106px;
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      width: 100%;
      li {
        float: left;
        display: inline-block;
        padding: 0px;
        &.clicks {
          padding-right: 10px;
          .container-span {
            font-family: "Roboto", sans-serif;
            color: #fff;
            font-size: 16px;
            font-weight: 500;
            background-color: #1971eb;
            border-radius: 12px;
            padding: 0px 10px;
            display: inline-flex;
            width: 80px;
            height: 22px;
            justify-content: center;
            align-items: center;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 11px;
              width: 70px;
            }
            svg {
              vertical-align: baseline;
              margin-right: 5px;
            }
          }
        }
        &.card-popover {
          a {
            padding: 0 5px;
            svg {
              width: 5px;
              max-height: none;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                width: 3px;
              }
            }
          }
        }
      }
    }
  }
`;
